import {
  Body1,
  Body2,
  Card,
  Display1,
  Display2,
  Display3,
  Grid,
  GridItem,
  Input,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Toggletip,
} from '@sainsburys-tech/fable'
import { SparkFilled } from '@sainsburys-tech/icons'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isSafari } from 'react-device-detect'

import { LastEditedTooltip } from '../../../common/components/LastEditedTooltip/LastEditedTooltip'
import { IPlan } from '../../types/IPlan'
import { IPlanItem } from '../../types/IPlanItem'
import { IPlannerGroup } from '../../types/IPlannerGroup'

interface IProps {
  testid: string
  plan: IPlan
  controlGroupId: number
  editing: boolean
  onProductionTargetChange: (
    planItemId: number,
    updatedQuantity: number,
  ) => void
}

const SalesPlannerTable = (props: IProps) => {
  const [localInputValues, setLocalInputValues] = useState<
    Record<number, string>
  >({})
  const focusedInputRef = useRef<string | null>(null);

  // Constants
  const ORIGINAL_FORECAST_TOOLTIP = 'The original Sales Forecast is shown in brackets.'

  const getSalesForecastTitle = (plan: IPlan) =>
    plan.eventName ? `${plan.eventName} Sales forecast` : 'Sales forecast'

  const getProductionTarget = (plan: IPlan) =>
    plan.eventName ? `${plan.eventName} Production target` : 'Production target'

  const getTables = () => {
    if (props.plan.planControlGroups === null) {
      return (
        <Body1 style={{ textAlign: 'center' }} data-testid='plan-not-found'>
          A plan was not found
        </Body1>
      )
    }

    if (props.plan.planControlGroups.length === 0) {
      return (
        <Body1
          style={{ textAlign: 'center' }}
          data-testid='plan-skus-not-found'
        >
          Plan skus were not found
        </Body1>
      )
    }

    const planControlGroup = props.plan.planControlGroups.find(
      (p) => p.controlGroupId === props.controlGroupId,
    )

    if (planControlGroup === undefined) {
      return (
        <Body1
          style={{ textAlign: 'center' }}
          data-testid='plan-control-group-not-found'
        >
          A plan was not found for this Control Group
        </Body1>
      )
    }

    return planControlGroup.plannerGroups
      .sort((planGroupA: IPlannerGroup, planGroupB: IPlannerGroup): number =>
        planGroupA.positionOnOutput < planGroupB.positionOnOutput ? -1 : 1,
      )
      .map((plannerGroup) => (
        <Card
          orientation='vertical'
          key={plannerGroup.plannerGroupId}
          data-testid='planner-group-card'
        >
          <div className='c-forecast-group-header'>
            <Display2>{plannerGroup.plannerGroupName}</Display2>
          </div>

          {/* Desktop/tablet view */}
          <div className='c-bakery-table-container c-bakery-forecast-table-wrapper forecast-table-scroll'>
            <Table
              layout={'auto'}
              isStriped
              className={`forecast-table ${isPastDay()
                ? 'variant-past'
                : isTodayOrFuture() && !isTodayOrPastDay()
                  ? 'variant-future'
                  : 'variant-today'
                }`}
              data-testid='forecast-table'
            >
              <TableHeader>
                <TableRow>
                  <TableHeaderCell className='col-sku'>
                    <Display1>Type/SKU</Display1>
                  </TableHeaderCell>
                  {isTodayOrFuture() && (
                    <TableHeaderCell
                      className='col-sales'
                      testid='sales-forecast-title'
                    >
                      <Display1>{getSalesForecastTitle(props.plan)}</Display1>
                    </TableHeaderCell>
                  )}
                  {isTodayOrPastDay() && (
                    <TableHeaderCell
                      className='col-target'
                      testid='production-target-title'
                    >
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.2em',
                        }}
                      >
                        <Display1 id='production-target-title'>
                          Production Target
                        </Display1>
                        <span className='c-production-target-tooltip-icon'>
                          <Toggletip
                            position='bottom-end'
                            label='Production Target'
                            message={productionTargetTooltipText}
                            testid='production-target-tooltip'
                          />
                        </span>
                      </span>
                    </TableHeaderCell>
                  )}
                  {isPastDay() && (
                    <TableHeaderCell
                      className='col-completion'
                      testid='completion-title'
                    >
                      <Display1>Confirmed completion</Display1>
                    </TableHeaderCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>{getTableRow(plannerGroup)}</TableBody>
              <TableFooter>
                <TableRow>
                  {!isTodayOrPastDay() && isTodayOrFuture() && (
                    <>
                      <TableCell />
                      <TableCell>
                        <div className='c-split-cell'>
                          <div
                            className='c-bakery-split-cell__left'
                            data-testid='total-produced-or-sales'
                            id='totalProducedOrSales'
                          >
                            <Body2>
                              {getTotalProduceOrSales(plannerGroup)}
                            </Body2>
                          </div>
                        </div>
                      </TableCell>
                    </>
                  )}
                  {isTodayOrPastDay() && !isPastDay() && (
                    <>
                      <TableCell />
                      <TableCell />
                      <TableCell>
                        <div className='c-split-cell'>
                          <div
                            className='c-bakery-split-cell__left'
                            data-testid='total-produced-or-sales'
                            id='totalProducedOrSales'
                          >
                            <Body2>
                              {getTotalProduceOrSales(plannerGroup)}
                            </Body2>
                          </div>
                        </div>
                      </TableCell>
                    </>
                  )}
                  {isPastDay() && (
                    <>
                      <TableCell />
                      <TableCell />
                      <TableCell>
                        <div className='c-split-cell'>
                          <div
                            className='c-bakery-split-cell__left'
                            data-testid='total-produced-or-sales'
                            id='totalProducedOrSales'
                          >
                            <Body2>
                              {getTotalProduceOrSales(plannerGroup)}
                            </Body2>
                          </div>
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableFooter>
            </Table>
          </div>
          {/* Mobile view */}
          <div className='c-bakery-card-container'>
            {getCards(plannerGroup)}
          </div>
        </Card>
      ))
  }

  const getTotalProduceOrSales = (plannerGroup: IPlannerGroup) => {
    let totalProduce = 0
    if (isTodayOrPastDay()) {
      plannerGroup.planItems.forEach(
        (planItem) => (totalProduce += planItem.produceQuantity),
      )
    } else {
      plannerGroup.planItems.forEach(
        (planItem) => (totalProduce += planItem.totalSalesForecast),
      )
    }

    return totalProduce
  }

  const isPastDay = () => dayjs(props.plan.planDate).diff(dayjs(), 'day') < 0

  const isTodayOrFuture = () =>
    dayjs(props.plan.planDate).diff(dayjs(), 'day') >= 0

  const isTodayOrPastDay = () =>
    dayjs(props.plan.planDate).diff(dayjs(), 'day', true) <= 0

  const shouldShowLastEdited = (planItem: IPlanItem) => 
    !!(planItem.updatedAt && planItem.updatedBy?.trim())

  const onProduceInput = useCallback((
    value: string,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const name = event?.target.name;
    const regexZeroToThreeDigitsOnly = /^\d{0,3}$/;

    if (regexZeroToThreeDigitsOnly.test(value) && name) {
      // Store which input should keep focus
      focusedInputRef.current = event?.target.id || null;

      props.onProductionTargetChange(Number(name), Number(value));
    }
  }, [props.onProductionTargetChange]);

  useEffect(() => {
    if (focusedInputRef.current) {
      // Try finding by ID first
      let element = document.getElementById(focusedInputRef.current);

      // If not found, try finding the input inside a wrapper with that ID
      if (!element || !(element instanceof HTMLInputElement)) {
        const wrapper = document.getElementById(focusedInputRef.current);
        element = wrapper?.querySelector('input') as HTMLInputElement;
      }

      if (element && element instanceof HTMLInputElement) {
        element.focus();
        element.setSelectionRange(element.value.length, element.value.length);
      } else {
      }
      focusedInputRef.current = null;
    }
  }, [props.plan]);

  const onProductInputFocus = useCallback((event?: React.FocusEvent<HTMLInputElement>) => {
    if (!event) return;
    if (isSafari) {
      try {
        event.currentTarget.focus();
        event.currentTarget.setSelectionRange(
          0,
          event.currentTarget.value.length,
        );
      } catch (ex) {
        console.log(ex);
        event.currentTarget.select();
      }
    } else {
      event.currentTarget.select();
    }
  }, [isSafari]);

  const productionTargetTooltipText = `Production target contains today's expected sales
    plus carryover for tomorrow's multiday life products.`

  const renderLastEditedTooltip = (planItem: IPlanItem) => {
    if (!shouldShowLastEdited(planItem)) return null;
    return LastEditedTooltip(
      planItem.updatedBy!,
      planItem.updatedAt!,
      planItem.originalSalesForecastWithUplift ??
        planItem.originalSalesForecast,
      ORIGINAL_FORECAST_TOOLTIP
    )
  }

  const getTableRow = useMemo(
    () => (plannerGroup: IPlannerGroup) => {
      return plannerGroup.planItems
        .sort((planItemA: IPlanItem, planItemB: IPlanItem): number =>
          planItemA.positionOnOutput < planItemB.positionOnOutput ? -1 : 1,
        )
        .map((planItem: IPlanItem) => {
          return (
            <TableRow key={`${plannerGroup.plannerGroupId}-${planItem.planItemId}`}>
              <TableCell className='col-sku'>
                <Body2>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <span>{planItem.skuName}</span>
                    {planItem.isCredibleProduct === true && (
                      <SparkFilled className='c-star__icon' />
                    )}
                  </span>
                </Body2>
              </TableCell>
              {isTodayOrFuture() && (
                <TableCell className='col-sales'>
                  {props.editing && planItem.isSkuEditable ? (
                    <Input
                      label='Sales forecast'
                      hasHiddenLabel
                      value={
                        localInputValues[planItem.planItemId] !== undefined
                          ? localInputValues[planItem.planItemId]
                          : planItem.totalSalesForecast.toString()
                      }
                      name={planItem.planItemId.toString()}
                      onChange={onProduceInput}
                      onFocus={onProductInputFocus}
                      onBlur={() => {
                        // Clear local state on blur and trigger final update
                        if (localInputValues[planItem.planItemId] !== undefined) {
                          props.onProductionTargetChange(
                            planItem.planItemId,
                            Number(localInputValues[planItem.planItemId])
                          );
                          setLocalInputValues(prev => {
                            const newValues = { ...prev };
                            delete newValues[planItem.planItemId];
                            return newValues;
                          });
                        }
                      }}
                      className='c-bakery-plan-item-input'
                      type='text'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      autocomplete='off'
                      id={planItem.planItemId.toString()}
                      testid='edit-forecast-input'
                    />
                  ) : (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5em',
                      }}
                    >
                      <Body2>{planItem.totalSalesForecast} &nbsp;</Body2>
                      {renderLastEditedTooltip(planItem)}
                    </span>
                  )}
                </TableCell>
              )}
              {isTodayOrPastDay() && (
                <TableCell className='col-target'>
                  <Body2>{planItem.produceQuantity}</Body2>
                </TableCell>
              )}
              {isPastDay() && (
                <TableCell className='col-completion'>
                  <Body2>
                    {planItem.quantityCompleted}/{planItem.produceQuantity}
                  </Body2>
                </TableCell>
              )}
            </TableRow>
          );
        });
    },
    [props.editing, isTodayOrFuture, isTodayOrPastDay, isPastDay, onProduceInput, onProductInputFocus]
  );

  const getCards = useMemo(
    () => (plannerGroup: IPlannerGroup) =>
      plannerGroup.planItems
        .sort((a, b) => a.positionOnOutput - b.positionOnOutput)
        .map((planItem: IPlanItem, i: number) => (
          <div
            key={planItem.planItemId}
            className={`c-bakery-card-row ${i % 2 !== 0 ? 'c-bakery-card-alt-row' : ''}`}
          >
            <Display3>{planItem.skuName}</Display3>

            {isTodayOrFuture() && (
              <Grid>
                <GridItem
                  className={
                    shouldShowLastEdited(planItem)
                      ? 'c-bakery-card-with-tooltip'
                      : 'c-bakery-card'
                  }
                  colSpan={6}
                >
                  {getSalesForecastTitle(props.plan)}
                </GridItem>

                {props.editing && planItem.isSkuEditable ? (
                  <GridItem colSpan={6}>
                    <Input
                      label="Sales forecast"
                      hasHiddenLabel
                      value={planItem.totalSalesForecast.toString()}
                      name={planItem.planItemId.toString()}
                      onChange={onProduceInput}
                      onFocus={onProductInputFocus}
                      className="c-bakery-plan-item-input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autocomplete="off"
                      id="editForecast"
                      testid="edit-forecast-input"
                    />
                  </GridItem>
                ) : (
                  <GridItem
                    className={shouldShowLastEdited(planItem) ? '' : 'c-bakery-card'}
                    colSpan={6}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      <Body2>{planItem.totalSalesForecast} &nbsp;</Body2>
                      {renderLastEditedTooltip(planItem)}
                    </span>
                  </GridItem>
                )}
              </Grid>
            )}

            {isTodayOrPastDay() && (
              <Grid>
                <GridItem className="c-bakery-card" colSpan={6}>
                  {getProductionTarget(props.plan)}
                </GridItem>
                <GridItem className="c-bakery-card" colSpan={6}>
                  {planItem.produceQuantity}
                </GridItem>
              </Grid>
            )}

            {isPastDay() && (
              <Grid>
                <GridItem className="c-bakery-card" colSpan={6}>
                  Confirmed Completion
                </GridItem>
                <GridItem className="c-bakery-card" colSpan={6}>
                  {planItem.quantityCompleted}/{planItem.produceQuantity}
                </GridItem>
              </Grid>
            )}
          </div>
        )),
    [
      props.editing,
      isTodayOrFuture,
      isTodayOrPastDay,
      isPastDay,
      onProduceInput,
      onProductInputFocus,
      props.plan,
    ]
  );

  return <div>{getTables()}</div>
}

export default SalesPlannerTable